import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';
import * as stngs from '~js/utils/settings';
import { RangeSlider } from '~js/components/range-slider';
import { Collapsible } from '~js/components/collapsible';
import { a11y } from '~js/components/a11y';
import { Ajaxify } from '~js/components/ajaxify';
import { debounce, makeSwatches, getSearchParams } from '~js/utils/utils';
import { ProductGrid } from '~js/components/product-grid';
import { setVars } from '~js/components/set-vars';
import { CustomScrollbar } from '~js/components/custom-scrollbar';
import { Tooltip } from '~js/components/tooltip';


export class Filters {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.enableFilters = container.dataset.enableFilters === 'true';
    this.enableSorting = container.dataset.enableSorting === 'true';
    this.filterMode = container.dataset.filterMode;
    this.collectionHandle = this.container.dataset.collection;
    this.isSearchPage = container.closest(s.selectors$U.searchPerformed) != null;
    this.productGrid = this.container.querySelector(s.selectors$U.productGrid);
    this.productsCount = this.container.querySelector(s.selectors$U.productsCount);
    this.groupTagFilters = this.container.querySelectorAll(s.selectors$U.filter);
    this.filters = this.container.querySelector(s.selectors$U.filters);
    this.filterTriggers = this.container.querySelectorAll(s.selectors$U.collapsibleTrigger);
    this.filtersStickyBar = this.container.querySelector(s.selectors$U.filtersStickyBar);
    this.filtersForm = this.container.querySelector(s.selectors$U.filtersForm);
    this.inputSort = this.container.querySelectorAll(s.selectors$U.inputSort);
    this.sortToggle = this.container.querySelector(s.selectors$U.sortToggle);
    this.collectionSortOptions = this.container.querySelector(s.selectors$U.collectionSortOptions);
    this.a11y = a11y;
    this.filterData = [];
    this.rangeSlider = null;
    this.sortDropdownEvent = () => this.sortDropdownToggle();
    this.onTabHandlerEvent = (event) => this.onTabHandler(event);
    this.updateCollectionFormSortEvent = (event) => this.updateCollectionFormSort(event);
    this.bodyClickEvent = (event) => this.bodyClick(event);
    this.onFilterResetClick = this.onFilterResetClick.bind(this);
    this.onFilterTagResetClick = this.onFilterTagResetClick.bind(this);
    this.onFilterTagClearClick = this.onFilterTagClearClick.bind(this);
    this.onFilterToggleClick = this.onFilterToggleClick.bind(this);
    this.onKeyUpHandler = this.onKeyUpHandler.bind(this);
    this.updateRangeEvent = this.updateRange.bind(this);
    this.debouncedSubmitEvent = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);
    this.debouncedSortEvent = debounce((event) => {
      this.onSortChange(event);
    }, 500);
    this.productGridEvents = {};

    if (this.filters) {
      this.hideFiltersDrawer = this.hideFiltersDrawer.bind(this);
      this.showFiltersDrawer = this.showFiltersDrawer.bind(this);
      this.resizeEvent = debounce(() => {
        this.filtersResizeEvents();
      }, 500);
      this.filtersResizeEvents();
      document.addEventListener('theme:resize:width', this.resizeEvent);
    }

    this.initTagFilters();
    this.initFacetedFilters();
    this.bindToggleButtonsEvents();
    this.bindFilterButtonsEvents();
    this.initProductGridEvents(theme.settings.enableInfinityScroll);

    makeSwatches(this.container);
    this.collapsible = new Collapsible(this.container);

    // Update css variable for collection sticky bar height
    setVars();

    window.addEventListener('popstate', this.onHistoryChange.bind(this));

    this.sortToggle?.addEventListener('click', this.sortDropdownEvent);

    document.addEventListener('click', this.bodyClickEvent);

    this.filterShowMore();
  }

  /*
  * Init faceted filters
  */
  initFacetedFilters() {
    if (this.filterMode == 'tag' || this.filterMode == 'group' || !this.enableFilters) {
      return;
    }

    this.rangeSlider = new RangeSlider(this.container);
  }

  /*
  * Init tooltips for swatches
  */
  initTooltips() {
    this.tooltips = this.container.querySelectorAll(s.selectors$U.tooltip);

    if (window.innerWidth < theme.sizes.small) {
      this.tooltips = this.productGrid?.querySelectorAll(s.selectors$U.tooltip);
    }

    this.tooltips?.forEach((tooltip) => {
      new Tooltip(tooltip);
    });

    this.handleVisibleTooltips();
  }

  handleVisibleTooltips() {
    if (this.tooltips.length > 0) {
      const tooltipTarget = document.querySelector(s.selectors$U.tooltipContainer);
      if (tooltipTarget.classList.contains(c.classes$K.isVisible)) {
        tooltipTarget.classList.remove(c.classes$K.isVisible);
      }
    }
  }

  /*
  * Price range slider update
  */
  updateRange() {
    const rangeMin = this.filtersForm.querySelector(s.selectors$U.rangeMin);
    const rangeMax = this.filtersForm.querySelector(s.selectors$U.rangeMax);
    const priceMin = this.filtersForm.querySelector(s.selectors$U.priceMin);
    const priceMax = this.filtersForm.querySelector(s.selectors$U.priceMax);

    if (rangeMin.hasAttribute(s.selectors$U.rangeMinValue) && rangeMax.hasAttribute(s.selectors$U.rangeMaxValue)) {
      const priceMinValue = parseFloat(priceMin.placeholder, 10);
      const priceMaxValue = parseFloat(priceMax.placeholder, 10);
      const rangeMinValue = parseFloat(rangeMin.getAttribute(s.selectors$U.rangeMinValue), 10);
      const rangeMaxValue = parseFloat(rangeMax.getAttribute(s.selectors$U.rangeMaxValue), 10);

      if (priceMinValue !== rangeMinValue || priceMaxValue !== rangeMaxValue) {
        priceMin.value = parseInt(rangeMinValue);
        priceMax.value = parseInt(rangeMaxValue);

        this.filtersForm.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  /*
  * Render product grid and filters on form submission
  */
  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(this.filtersForm);
    const searchParams = new URLSearchParams(formData);
    const deleteParams = [];
    let searchParamsString = '';

    if (this.isSearchPage) {
      this.searchForm = this.container.querySelector(s.selectors$U.searchForm);
      this.currentType = this.container.getAttribute(a.attributes$C.currentType);
    }

    // if submitted price equal to price range min and max remove price parameters
    const rangeMin = this.filtersForm.querySelector(s.selectors$U.rangeMin);
    const rangeMax = this.filtersForm.querySelector(s.selectors$U.rangeMax);
    const priceMin = this.filtersForm.querySelector(s.selectors$U.priceMin);
    const priceMax = this.filtersForm.querySelector(s.selectors$U.priceMax);
    const checkElements = rangeMin && rangeMax && priceMin && priceMax;

    if (
      checkElements &&
      rangeMin.hasAttribute(s.selectors$U.rangeMinDefault) &&
      rangeMax.hasAttribute(s.selectors$U.rangeMaxDefault)
    ) {
      const rangeMinDefault = parseFloat(rangeMin.getAttribute(s.selectors$U.rangeMinDefault), 10);
      const rangeMaxDefault = parseFloat(rangeMax.getAttribute(s.selectors$U.rangeMaxDefault), 10);
      const priceMinValue = !priceMin.value ? rangeMinDefault : parseFloat(priceMin.value, 10);
      const priceMaxValue = !priceMax.value ? rangeMaxDefault : parseFloat(priceMax.value, 10);

      if (priceMinValue <= rangeMinDefault && priceMaxValue >= rangeMaxDefault) {
        deleteParams.push('filter.v.price.gte');
        deleteParams.push('filter.v.price.lte');
        searchParams.delete('filter.v.price.gte');
        searchParams.delete('filter.v.price.lte');
      }
    }

    searchParamsString = searchParams.toString();

    if (this.isSearchPage) {
      searchParamsString = getSearchParams(this.searchForm, this.filtersForm, deleteParams);

      let typeString = '';
      if (this.currentType === 'all') typeString = '&type=product';
      if (searchParamsString.indexOf('&type=product') > -1) typeString = '';
      searchParamsString += typeString;
    }

    this.renderSection(searchParamsString, event);
  }

  /*
  * Call renderSection on history change
  */
  onHistoryChange(event) {
    if (!this.filters) return;

    let searchParams = event.state?.searchParams || '';

    if (this.isSearchPage) {
      if (!event.state) searchParams = window.location.search;
      const shouldRenderSearchResults = searchParams.indexOf('type=product') > -1;

      if (!shouldRenderSearchResults) return;
    }

    this.renderSection(searchParams, null, false);
  }

  /*
  * Render section on history change or filter/sort change event
  */
  renderSection(searchParams, event, updateURLHash = true) {
    this.startLoading();
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
    const filterDataUrl = (element) => element.url === url;
    this.filterData.some(filterDataUrl)
      ? this.renderSectionFromCache(filterDataUrl, event)
      : this.renderSectionFromFetch(url, event);

    if (updateURLHash) {
      this.updateURLHash(searchParams);
    }
  }

  /*
  * Render section from fetch call
  */
  renderSectionFromFetch(url) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        this.filterData = [...this.filterData, { html, url }];
        this.inputSort = this.container.querySelectorAll(s.selectors$U.inputSort);
        this.renderFilters(html);
        this.bindFilterButtonsEvents();
        this.hideFiltersOnMobile();
        this.renderProductGrid(html);
        this.updateProductsCount(html);
        this.finishLoading();
        this.mobileFiltersScrollLock();
        this.handleSearchPageActiveTab();
      });
  }

  /*
  * Render section from Cache
  */
  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html;
    this.renderFilters(html, event);
    this.hideFiltersOnMobile();
    this.renderProductGrid(html);
    this.updateProductsCount(html);
    this.finishLoading();
    this.mobileFiltersScrollLock();
    this.handleSearchPageActiveTab();
  }

  handleSearchPageActiveTab() {
    if (!this.isSearchPage) return;

    this.scrollable = this.container.querySelector(s.selectors$U.scrollable);
    if (!this.scrollable || this.customScrollbar) return;
    this.customScrollbar = new CustomScrollbar(this.container);
  }

  /*
  * Render product grid items on fetch call
  */
  renderProductGrid(html) {
    const newProductGrid = new DOMParser().parseFromString(html, 'text/html').querySelector(s.selectors$U.productGrid);

    if (!newProductGrid) {
      return;
    }

    this.productGrid.innerHTML = newProductGrid.innerHTML;

    this.initProductGridEvents(theme.settings.enableInfinityScroll);
    this.filterShowMore();
  }

  /*
  * Update total number of products on fetch call
  */
  updateProductsCount(html) {
    const newProductsCount = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(s.selectors$U.productsCount);

    if (!newProductsCount) {
      return;
    }

    this.productsCount.innerHTML = newProductsCount.innerHTML;
  }

  /*
  * Render filters on fetch call
  */
  renderFilters(html) {
    const newFilters = new DOMParser().parseFromString(html, 'text/html').querySelector(s.selectors$U.filters);

    if (!newFilters) {
      return;
    }

    this.filters.innerHTML = newFilters.innerHTML;
    this.filtersForm = document.querySelector(s.selectors$U.filtersForm);
    this.bindFilterButtonsEvents();
    this.bindToggleButtonsEvents();
    makeSwatches(this.container);
    this.collapsible = new Collapsible(this.container);

    // Init price range slider
    document.dispatchEvent(new CustomEvent('theme:filters:init', { bubbles: true }));
  }

  /*
  * Update URL when filter/sort is changed
  */
  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  /*
  * Bind filter buttons events
  */
  bindFilterButtonsEvents() {
    if (this.inputSort.length > 0) {
      this.inputSort.forEach((input) => {
        input.addEventListener('change', this.updateCollectionFormSortEvent);
      });
    }

    if (this.filtersForm) {
      this.filtersForm.addEventListener('input', this.debouncedSubmitEvent.bind(this));

      this.filtersForm.addEventListener('theme:filter:range-update', this.updateRangeEvent);
    }

    if (this.collectionSortOptions) {
      this.collectionSortOptions.addEventListener('keyup', this.onTabHandlerEvent);
    }

    if (this.filterMode == 'tag' || this.filterMode == 'group' || !this.enableFilters) {
      return;
    }

    this.container.querySelectorAll(s.selectors$U.filterResetButton).forEach((button) => {
      button.addEventListener('click', this.onFilterResetClick, { once: true });
    });
  }

  /*
  * Render products on specific filter click event
  */
  onFilterResetClick(event) {
    event.preventDefault();
    this.renderSection(new URL(event.currentTarget.href).searchParams.toString());
  }

  /*
  * Bind filter title click events to toggle options visibility
  */
  bindToggleButtonsEvents() {
    this.container.querySelectorAll(s.selectors$U.toggleFilters).forEach((button) => {
      button.addEventListener('click', this.onFilterToggleClick);
    });
    this.container.querySelectorAll(s.selectors$U.closeFilters).forEach((button) => {
      button.addEventListener('click', this.hideFiltersDrawer);
    });
    this.container.querySelectorAll(s.selectors$U.openFilters).forEach((button) => {
      button.addEventListener('click', this.showFiltersDrawer);
    });

    this.container.querySelector(s.selectors$U.collectionWrapper)?.addEventListener('keyup', this.onKeyUpHandler);
  }

  onTabHandler(event) {
    if (
      event.code === theme.keyboardKeys.SPACE ||
      event.code === theme.keyboardKeys.ENTER ||
      event.code === theme.keyboardKeys.NUMPADENTER
    ) {
      const newSortValue = event.target.previousElementSibling.value;

      this.filtersForm.querySelectorAll(s.selectors$U.inputSort).forEach((input) => {
        if (input.checked) {
          input.checked = false;
        }
        if (input.value === newSortValue) {
          input.checked = true;
        }
      });

      this.filtersForm.dispatchEvent(new Event('input', { bubbles: true }));
      event.target.dispatchEvent(new Event('click', { bubbles: true }));
    }
  }

  /*
  * Event handler on user ESC key press
  */
  onKeyUpHandler(event) {
    if (event.code === theme.keyboardKeys.ESCAPE) {
      this.hideFiltersDrawer();
    }
  }

  /*
  * Toggle filter options on title click
  */
  onFilterToggleClick(event) {
    event.preventDefault();
    setVars(); // Update css variables for correct filters drawer height

    const filtersVisible = this.filters.classList.contains(c.classes$K.collectionFiltersVisible);

    filtersVisible ? this.hideFiltersDrawer() : this.showFiltersDrawer();
  }

  sortDropdownToggle() {
    if (!this.collectionSortOptions) return;

    this.collectionSortOptions.classList.toggle(c.classes$K.collectionSortOptionWrapperVisible);
  }

  /*
  * Close the sort dropdown on button click outside the dropdown (for desktop)
  */
  bodyClick(event) {
    if (!this.collectionSortOptions) return;

    const isSortBar = this.sortToggle.contains(event.target);
    const isVisible = this.collectionSortOptions.classList.contains(c.classes$K.collectionSortOptionWrapperVisible);

    if (isVisible && !isSortBar) {
      this.sortDropdownToggle();
    }
  }

  updateCollectionFormSort(event) {
    const target = event.target;
    const newSortValue = target.value;
    const secondarySortBy = target.closest(s.selectors$U.collectionSortOptions);

    this.container.querySelectorAll(s.selectors$U.inputSort).forEach((input) => {
      if (input.value === newSortValue) {
        input.checked = true;
      }
    });

    if (secondarySortBy !== null) {
      this.filtersForm.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /*
  * Scroll down and open collection filters if they are hidden
  */
  showFiltersDrawer() {
    const instance = this;

    this.a11y.state.trigger = document.querySelector(s.selectors$U.toggleFilters);

    // Trap focus
    this.a11y.trapFocus({
      container: instance.filters,
    });

    this.mobileFiltersScrollLock();
  }

  /*
  * Scroll lock activation for filters drawer (on mobile)
  */
  mobileFiltersScrollLock() {
    // Open filters and scroll lock if only they are hidden on lower sized screens
    if (window.innerWidth < theme.sizes.small) {
      const scrollableElement = document.querySelector(s.selectors$U.filtersList);

      if (!this.filters.classList.contains(c.classes$K.collectionFiltersVisible)) {
        this.filters.classList.add(c.classes$K.collectionFiltersVisible);
      }

      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: scrollableElement }));
    }
  }

  /*
  * Hide filter accordion elements on mobile
  */
  hideFiltersOnMobile() {
    const filterTriggers = this.container.querySelectorAll(
      `${s.selectors$U.collapsibleTrigger}:not(${s.selectors$U.showMoreTrigger})`
    );

    if (window.innerWidth < theme.sizes.small) {
      requestAnimationFrame(() => {
        filterTriggers.forEach((element) => {
          const isFilterActive = element.getAttribute(a.attributes$C.filterActive) === 'true';

          if (element.classList.contains(c.classes$K.isExpanded) && !isFilterActive) {
            element.dispatchEvent(new Event('click'));
          }
        });
      });
    }
  }

  /*
  * Show filter accordion elements on desktop if they should be opened by default
  */
  showFiltersOnDesktop() {
    const filterTriggers = this.container.querySelectorAll(
      `${s.selectors$U.collapsibleTrigger}:not(${s.selectors$U.showMoreTrigger})`
    );

    // "Default filter layout" states
    const filtersDefaultState = this.container.getAttribute(a.attributes$C.filtersDefaultState);
    const openFirstFilterOnly = filtersDefaultState === 'first-open';
    const openAllFilters = filtersDefaultState === 'open';
    const closeAllFilters = filtersDefaultState === 'closed';
    // When sorting is enabled the first `${filterTrigger}` element on mobile is a 'Sort by' button
    const firstTriggerIndex = this.enableSorting ? 1 : 0;

    filterTriggers.forEach((element, index) => {
      const isCurrentFilterExpanded = element.classList.contains(c.classes$K.isExpanded);
      const isCurrentFilterActive = element.getAttribute(a.attributes$C.filterActive) === 'true';
      // 'first-open' state conditions
      const isFirstClosed = !isCurrentFilterExpanded && index === firstTriggerIndex;
      const allElseExpanded = isCurrentFilterExpanded && index !== firstTriggerIndex;
      const shouldOpenFirst = openFirstFilterOnly && isFirstClosed;
      const shouldCloseAllExceptFirst = openFirstFilterOnly && allElseExpanded;
      // 'open' state conditions
      const shouldOpenAllClosedOnes = openAllFilters && !isCurrentFilterExpanded;
      const shouldOpenActiveOnes = isCurrentFilterActive && !isCurrentFilterExpanded && openAllFilters;
      // 'close' state conditions
      const shouldCloseExpandedOnes = closeAllFilters && isCurrentFilterExpanded;

      if (isCurrentFilterActive && !shouldOpenActiveOnes) return;

      if (
        shouldCloseExpandedOnes ||
        shouldOpenFirst ||
        shouldCloseAllExceptFirst ||
        shouldOpenAllClosedOnes ||
        shouldOpenActiveOnes
      ) {
        element.dispatchEvent(new Event('click'));
      }
    });
  }

  /*
  * Hide filters drawer
  */
  hideFiltersDrawer() {
    let filtersVisible = this.filters.classList.contains(c.classes$K.collectionFiltersVisible);
    let loading = this.container.classList.contains(c.classes$K.isLoading);

    if (filtersVisible) {
      this.filters.classList.remove(c.classes$K.collectionFiltersVisible);
      this.a11y.removeTrapFocus();
    }

    // Enable page scroll if no loading state
    if (!loading) {
      document.dispatchEvent(
        new CustomEvent('theme:scroll:unlock', { bubbles: true, detail: stngs.settings$8.loadingTimeout })
      );
    }
  }

  /*
  * Hiding the filters drawer on desktop & hiding the filters on mobile (showing only filter title)
  */
  filtersResizeEvents() {
    if (window.innerWidth >= theme.sizes.small) {
      this.showFiltersOnDesktop();
      this.hideFiltersDrawer();
    } else {
      this.hideFiltersOnMobile();
    }
  }

  /*
  * Show more functionality
  */
  filterShowMore() {
    this.showMore = this.container.querySelectorAll(s.selectors$U.showMore);
    if (this.showMore.length === 0) return;

    this.showMore.forEach((element) => {
      const filterCollapsibleTrigger = element.querySelector(s.selectors$U.collapsibleTrigger);
      const showMoreActions = element.querySelector(s.selectors$U.showMoreActions);

      if (!showMoreActions) return;

      const showMoreTrigger = showMoreActions.querySelector(s.selectors$U.showMoreTrigger);
      const showMoreContainer = showMoreActions.querySelector(s.selectors$U.showMoreContainer);
      const focusable = showMoreContainer.querySelectorAll(window.theme.focusable);
      const isShowMoreContainerExpanded = showMoreContainer.getAttribute(a.attributes$C.ariaExpanded) === 'true';

      if (!isShowMoreContainerExpanded) {
        focusable.forEach((item) => {
          item.setAttribute(a.attributes$C.tabIndex, '-1');
        });
      }

      showMoreTrigger.addEventListener('keyup', (event) => {
        if (
          event.code === theme.keyboardKeys.SPACE ||
          event.code === theme.keyboardKeys.ENTER ||
          event.code === theme.keyboardKeys.NUMPADENTER
        ) {
          this.updateShowMoreFocusableElements(event, focusable);
        }
      });
      showMoreTrigger.addEventListener('click', (event) => {
        this.updateShowMoreFocusableElements(event, focusable);
      });

      filterCollapsibleTrigger.addEventListener('keyup', (event) => {
        if (
          event.code === theme.keyboardKeys.SPACE ||
          event.code === theme.keyboardKeys.ENTER ||
          event.code === theme.keyboardKeys.NUMPADENTER
        ) {
          this.updateCollapsedContainerFocusableElements(filterCollapsibleTrigger, showMoreTrigger, focusable);
        }
      });
      filterCollapsibleTrigger.addEventListener('click', () => {
        this.updateCollapsedContainerFocusableElements(filterCollapsibleTrigger, showMoreTrigger, focusable);
      });
    });
  }

  /*
  * A11y: Update tabindex for all focusable elements in show-more collapsible containers,
  * on opening and closing events of their parent collapsible container
  * Solves wrongful tabbing in cases where a collapsible content is opened,
  * but it is located in another parent collapsible container that is closed
  */
  updateCollapsedContainerFocusableElements(filterCollapsibleTrigger, showMoreTrigger, focusable) {
    requestAnimationFrame(() => {
      const isFilterExpanded = filterCollapsibleTrigger.getAttribute(a.attributes$C.ariaExpanded) === 'true';
      const isShowMoreExpanded = showMoreTrigger.getAttribute(a.attributes$C.ariaExpanded) === 'true';

      focusable.forEach((item) => {
        if (!isFilterExpanded && isShowMoreExpanded) {
          item.setAttribute(a.attributes$C.tabIndex, '-1');
        }

        if (isFilterExpanded && isShowMoreExpanded) {
          item.removeAttribute(a.attributes$C.tabIndex);
        }
      });
    });
  }

  /*
  * A11y: Update tabindex for all focusable elements in show-more collapsible containers on opening and closing events
  * Double requestAnimationFrame method is used to make sure the collapsible content has already been expanded
  */
  updateShowMoreFocusableElements(event, focusable) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const isExpanded = event.target.getAttribute(a.attributes$C.ariaExpanded) === 'true';

        focusable.forEach((item, index) => {
          if (isExpanded) {
            item.removeAttribute(a.attributes$C.tabIndex);

            if (index === 0) item.focus();
            return;
          }
          item.setAttribute(a.attributes$C.tabIndex, '-1');
        });
      });
    });
  }

  /*
  * Init functions required when "Filter by tag/group" is selected from Collection page -> Collection pages -> Filter mode
  */
  initTagFilters() {
    if ((this.filterMode != 'tag' && this.filterMode != 'group') || !this.enableFilters) {
      return;
    }

    this.tags = this.container.dataset.tags.split('+').filter((item) => item);
    this.bindFilterTagButtonsEvents();
    this.bindSortChangeEvent();
  }

  /*
  * Render products when tag filter is selected
  */
  renderTagFiltersProducts(url) {
    this.startLoading();

    if (typeof this.endlessCollection === 'object') {
      this.endlessCollection.unload();
    }

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        const parsedData = new DOMParser().parseFromString(html, 'text/html');
        const productsHTML = parsedData.querySelector(s.selectors$U.productGrid).innerHTML;
        const filtersHTML = parsedData.querySelector(s.selectors$U.filters).innerHTML;

        this.productGrid.innerHTML = productsHTML;
        this.filters.innerHTML = filtersHTML;
        this.inputSort = this.container.querySelectorAll(s.selectors$U.inputSort);
        this.filtersForm = document.querySelector(s.selectors$U.filtersForm);
        this.filterData = [...this.filterData, { html, url }];
        this.alreadyClicked = false;

        this.bindFilterTagButtonsEvents();
        this.bindFilterButtonsEvents();
        this.bindSortChangeEvent();
        this.bindToggleButtonsEvents();
        this.initProductGridEvents(theme.settings.enableInfinityScroll);
        this.updateProductsCount(html);
        this.mobileFiltersScrollLock();
        this.hideFiltersOnMobile();
        makeSwatches(this.container);
        this.collapsible = new Collapsible(this.container);
        this.filterShowMore();

        // Update page URL if supported by the browser
        if (history.replaceState) {
          window.history.pushState({ path: url }, '', url);
        }
      })
      .catch((error) => {
        this.finishLoading();
        console.log(`Error: ${error}`);
      });
  }

  /*
  * Bind Filter by tag buttons
  */
  bindFilterTagButtonsEvents() {
    this.container.querySelectorAll(s.selectors$U.filterTagButton).forEach((button) => {
      button.addEventListener('click', this.onFilterTagButtonClick.bind(this));
    });

    this.container.querySelectorAll(s.selectors$U.filterTagClearButton).forEach((button) => {
      button.addEventListener('click', this.onFilterTagClearClick);
    });

    this.container.querySelectorAll(s.selectors$U.filterResetButton).forEach((button) => {
      button.addEventListener('click', this.onFilterTagResetClick);
    });
  }

  /*
  * Bind input Sort by change event for "filters by tag/group" only
  */
  bindSortChangeEvent() {
    this.container.querySelectorAll(s.selectors$U.inputSort).forEach((input) => {
      input.addEventListener('input', this.debouncedSortEvent.bind(this));
    });
  }

  /*
  * Filter by tag buttons click event
  */
  onFilterTagButtonClick(event) {
    event.preventDefault();
    if (this.alreadyClicked) {
      return;
    }
    this.alreadyClicked = true;
    const button = event.currentTarget;
    const selectedTag = button.dataset.tag;
    let isTagSelected = button.parentNode.classList.contains(c.classes$K.isActive);

    if (isTagSelected) {
      let tagIndex = this.tags.indexOf(selectedTag);

      button.parentNode.classList.remove(c.classes$K.isActive);

      if (tagIndex > -1) {
        this.tags.splice(tagIndex, 1);
      }
    } else {
      button.parentNode.classList.add(c.classes$K.isActive);

      this.tags.push(selectedTag);
    }

    let url = this.collectionHandle + '/' + this.tags.join('+') + '?sort_by=' + this.getSortValue();

    // Close filters dropdown on tag select
    this.container.querySelector(s.selectors$U.filter).classList.remove(c.classes$K.isExpanded);
    this.container.querySelector(s.selectors$U.filter).setAttribute(a.attributes$C.ariaExpanded, false);
    this.container.setAttribute('data-tags', '[' + this.tags + ']');
    this.renderTagFiltersProducts(url);
  }

  /*
  * Remove a specific tag filter
  */
  onFilterTagClearClick(event) {
    event.preventDefault();
    if (this.alreadyClicked) {
      return;
    }
    this.alreadyClicked = true;
    const button = event.currentTarget;
    const selectedTag = button.dataset.tag;
    const tagIndex = this.tags.indexOf(selectedTag);

    if (tagIndex > -1) {
      this.tags.splice(tagIndex, 1);
    }
    const url = this.collectionHandle + '/' + this.tags.join('+') + '?sort_by=' + this.getSortValue();

    this.container.setAttribute('data-tags', '[' + this.tags + ']');
    this.renderTagFiltersProducts(url);
  }

  /*
  * Re-render products with the new sort option selected
  */
  onSortChange() {
    let url = this.collectionHandle + '/' + this.tags.join('+') + '?sort_by=' + this.getSortValue();

    this.renderTagFiltersProducts(url);
  }

  /*
  * Get the selected sort option value
  */
  getSortValue() {
    let sortValue = '';
    this.inputSort.forEach((input) => {
      if (input.checked) {
        sortValue = input.value;
      }
    });

    return sortValue;
  }

  /*
  * Filter by tag reset button click event
  */
  onFilterTagResetClick(event) {
    event?.preventDefault();

    if (this.alreadyClicked) {
      return;
    }
    this.alreadyClicked = true;

    this.container.querySelectorAll(s.selectors$U.filterTag).forEach((element) => {
      element.classList.remove(c.classes$K.isActive);
    });

    this.container.querySelectorAll(s.selectors$U.filter).forEach((element) => {
      element.classList.remove(c.classes$K.isExpanded);
      element.setAttribute(a.attributes$C.ariaExpanded, false);
    });

    // Reset saved tags
    this.tags = [];
    this.container.setAttribute('data-tags', '');

    let url = this.collectionHandle + '/?sort_by=' + this.getSortValue();

    this.renderTagFiltersProducts(url);
  }

  /*
  * Get products container top position
  */
  getProductsOffsetTop() {
    return (
      this.productGrid.getBoundingClientRect().top -
      document.body.getBoundingClientRect().top -
      this.filtersStickyBar.offsetHeight
    );
  }

  /*
  * Get collection page sticky bar top position
  */
  getStickyBarOffsetTop() {
    return this.filtersStickyBar.getBoundingClientRect().top - document.body.getBoundingClientRect().top;
  }

  /*
  * Init all the events required on product grid items
  */
  initProductGridEvents(infinityScroll) {
    if (infinityScroll) {
      this.initInfinityScroll();
      this.initProductGridEvents(false);
      return;
    }

    this.productGridEvents = new ProductGrid(this.container);

    this.initTooltips();

    // Stop loading animation
    setTimeout(() => {
      this.finishLoading();
    }, stngs.settings$8.loadingTimeout * 1.5);
  }

  /*
  * Init Infinity scroll functionality
  */
  initInfinityScroll() {
    const callback = () => this.initProductGridEvents(false);

    // For Search page filters, infinity scroll is mostly handled in Tabs
    if (this.isSearchPage) {
      if (!this.enableFilters) return;

      document.dispatchEvent(
        new CustomEvent('theme:tab:ajaxinate', {
          bubbles: true,
          detail: 'product',
        })
      );

      return;
    }

    if (typeof this.endlessCollection === 'object') {
      this.endlessCollection.unload();
    }
    this.endlessCollection = new Ajaxify(this.container);

    if (this.endlessCollection.endlessScroll.length === 0) return;
    this.endlessCollection.endlessScroll[0].settings.callback = callback;
  }

  /*
  * Show loading animation and lock body scroll
  */
  startLoading() {
    this.container.classList.add(c.classes$K.isLoading);

    if (window.innerWidth >= theme.sizes.small) {
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
    }

    let productsTop = this.getProductsOffsetTop();

    window.scrollTo({
      top: productsTop,
      left: 0,
      behavior: 'smooth',
    });
  }

  /*
  * Hide loading animation and unlock body scroll
  */
  finishLoading() {
    const popups = document.querySelectorAll(`${s.selectors$U.popupsSection} .${c.classes$K.popupVisible}`);
    const isPopupActive = popups.length > 0;

    this.container.classList.remove(c.classes$K.isLoading);

    // Unlock the scroll unless there is a visible popup or there are only popups of types 'bar' and 'cookie'
    if (isPopupActive) {
      let preventScrollPopupsCount = 0;
      [...popups].forEach((popup) => {
        if (popup.hasAttribute(a.attributes$C.preventScrollLock)) {
          preventScrollPopupsCount += 1;
        }
      });

      if (preventScrollPopupsCount === popups.length) {
        document.dispatchEvent(
          new CustomEvent('theme:scroll:unlock', { bubbles: true, detail: stngs.settings$8.loadingTimeout })
        );
      }
    } else if (window.innerWidth >= theme.sizes.small) {
      document.dispatchEvent(
        new CustomEvent('theme:scroll:unlock', { bubbles: true, detail: stngs.settings$8.loadingTimeout })
      );
    }
  }

  /*
  * On block:deselect event
  */
  onDeselect() {
    if (this.productGridEvents) {
      this.productGridEvents.onDeselect();
    }
  }

  /*
  * On section:unload event
  */
  onUnload() {
    if (typeof this.endlessCollection === 'object') {
      this.endlessCollection.unload();
    }

    if (this.productGridEvents) {
      this.productGridEvents.onUnload();
    }

    if (this.collapsible) {
      this.collapsible.onUnload();
    }

    if (this.rangeSlider) {
      this.rangeSlider.unload();
    }

    if (this.filters) {
      document.removeEventListener('theme:resize:width', this.resizeEvent);
    }
    document.removeEventListener('click', this.bodyClickEvent);

    if (this.groupTagFilters.length > 0) {
      this.onFilterTagResetClick();
    }

    this.finishLoading();
  }
}